/** Bulk project import: download the template, fill it out, upload, preview, apply.
 *
 * The preview is not optional — choosing a file runs it immediately, and the Import
 * button stays disabled while any row has a problem. Nothing is written from a preview,
 * so an admin can iterate on the file as many times as it takes.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, Upload } from 'lucide-react'

import { Button, ErrorState, Pill } from '@/components/ui'
import { api } from '@/lib/api/client'

interface ImportRowReport {
  row: number
  name: string
  action: 'create' | 'skip_exists' | 'error'
  problems: string[]
  team: string
  category: string
  owner: string
  stage: string
}

interface ImportReport {
  rows: ImportRowReport[]
  counts: { create: number; skipExists: number; error: number }
  applied: boolean
}

const ACTION_TONE = { create: 'green', skip_exists: 'neutral', error: 'red' } as const
const ACTION_LABEL = {
  create: 'Will create',
  skip_exists: 'Already exists',
  error: 'Problem',
} as const

export function ProjectImportTab() {
  const client = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)

  const download = useMutation({
    mutationFn: async () => {
      const { data } = await api.GET('/api/v1/admin/project-import/template', {
        parseAs: 'blob',
      })
      return data as Blob
    },
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'wheeler-pmt-project-import.xlsx'
      anchor.click()
      URL.revokeObjectURL(url)
    },
  })

  const upload = useMutation({
    mutationFn: async ({ chosen, apply }: { chosen: File; apply: boolean }) => {
      const form = new FormData()
      form.append('file', chosen)
      const { data } = await api.POST('/api/v1/admin/project-import', {
        params: { query: { apply } },
        body: form as never,
        // FormData must reach fetch untouched — the default serializer would JSON it.
        bodySerializer: (body: unknown) => body as FormData,
      })
      return data as unknown as ImportReport
    },
    onSuccess: (result) => {
      setReport(result)
      if (result.applied) {
        void client.invalidateQueries({ queryKey: ['projects'] })
      }
    },
  })

  const choose = (chosen: File | null) => {
    setFile(chosen)
    setReport(null)
    if (chosen) upload.mutate({ chosen, apply: false })
  }

  const counts = report?.counts

  return (
    <div className="max-w-[900px]">
      <p className="mb-4 text-sm text-fg-muted">
        Download the template, fill out one project per row, and upload it back. The
        template's Reference sheet lists the teams, categories and people that exist right
        now. Nothing is imported until the preview below shows no problems and you press
        Import — and uploading the same file twice is safe, because rows whose project
        already exists are skipped.
      </p>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button onClick={() => download.mutate()} disabled={download.isPending}>
          <Download size={14} aria-hidden />
          {download.isPending ? 'Building…' : 'Download template'}
        </Button>
        <label className="cursor-pointer">
          <span className="sr-only">Choose a filled-out template</span>
          <input
            type="file"
            accept=".xlsx"
            className="text-sm text-fg-muted file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-fg"
            onChange={(event) => choose(event.target.files?.[0] ?? null)}
          />
        </label>
      </div>

      {download.error ? (
        <div className="mb-4">
          <ErrorState error={download.error} />
        </div>
      ) : null}
      {upload.error ? (
        <div className="mb-4">
          <ErrorState error={upload.error} />
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <span className="text-sm text-fg">
              {report.applied
                ? `Imported ${counts?.create ?? 0} project(s).`
                : `${counts?.create ?? 0} to create · ${counts?.skipExists ?? 0} already exist · ${counts?.error ?? 0} with problems`}
            </span>
            {!report.applied && file ? (
              <Button
                variant="primary"
                disabled={
                  upload.isPending || (counts?.error ?? 1) > 0 || (counts?.create ?? 0) === 0
                }
                onClick={() => upload.mutate({ chosen: file, apply: true })}
              >
                <Upload size={14} aria-hidden />
                {upload.isPending
                  ? 'Importing…'
                  : `Import ${counts?.create ?? 0} project(s)`}
              </Button>
            ) : null}
          </div>
          {!report.applied && (counts?.error ?? 0) > 0 ? (
            <p className="mb-3 text-xs" style={{ color: 'var(--status-red)' }}>
              Fix the rows marked below in the file, then choose it again. Nothing is
              imported while any row has a problem.
            </p>
          ) : null}

          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-3 py-2 font-semibold text-fg-muted">Row</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Project</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Result</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Team</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Category</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Owner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {report.rows.map((row) => (
                  <tr key={row.row}>
                    <td className="px-3 py-2 text-fg-muted">{row.row}</td>
                    <td className="px-3 py-2 text-fg">
                      {row.name}
                      {row.problems.length > 0 ? (
                        <ul className="mt-1 list-disc pl-4 text-xs" style={{ color: 'var(--status-red)' }}>
                          {row.problems.map((problem) => (
                            <li key={problem}>{problem}</li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <Pill tone={ACTION_TONE[row.action]}>{ACTION_LABEL[row.action]}</Pill>
                    </td>
                    <td className="px-3 py-2 text-fg-muted">{row.team}</td>
                    <td className="px-3 py-2 text-fg-muted">{row.category}</td>
                    <td className="px-3 py-2 text-fg-muted">{row.owner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  )
}
